import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MailCheck, Link2, Check, X } from "lucide-react";

const fieldWrapperClasses =
  "flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 transition focus-within:border-slate-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-400";

const inputClasses =
  "flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0";

const primaryButtonClasses =
  "inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition-colors duration-200 transition-transform hover:-translate-y-1 hover:scale-105 hover:from-slate-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60";

export const VerifyEmailPrompt = ({ embedded = false, defaultEmail = "", onClose } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialEmail = useMemo(() => {
    if (defaultEmail) return defaultEmail;
    const params = new URLSearchParams(location.search);
    const qsEmail = params.get("email");
    if (qsEmail) return qsEmail;
    const state = location.state;
    if (state && typeof state === "object" && "email" in state) {
      const value = state.email;
      return typeof value === "string" ? value : "";
    }
    return "";
  }, [defaultEmail, location.search, location.state]);

  const [verificationTokenInput, setVerificationTokenInput] = useState("");
  const [verificationTokenError, setVerificationTokenError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    } else {
      navigate("/", { replace: true });
    }
  };

  const extractTokenFromInput = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    let tokenCandidate = trimmed;
    try {
      const maybeUrl = new URL(trimmed);
      const segments = maybeUrl.pathname.split("/").filter(Boolean);
      if (segments.length) tokenCandidate = segments[segments.length - 1];
    } catch {
      /* ignore non-url values */
    }
    return tokenCandidate.replace(/[^A-Za-z0-9-_]/g, "");
  };

  const handleManualVerificationSubmit = (event) => {
    event.preventDefault();
    const token = extractTokenFromInput(verificationTokenInput);
    if (!token || token.length < 12) {
      setVerificationTokenError("Paste the verification code exactly as it appears in the email.");
      setSubmitted(false);
      return;
    }
    setVerificationTokenError(null);
    setSubmitted(true);
    navigate(`/verify-email/${token}/`);
  };

  const onInputChange = (event) => {
    setVerificationTokenInput(event.target.value);
    if (verificationTokenError) setVerificationTokenError(null);
    if (submitted) setSubmitted(false);
  };

  const cardPadding = embedded ? "p-6 sm:p-8" : "p-10";
  const card = (
    <div className={`relative mx-auto w-full max-w-md rounded-2xl border border-white/15 bg-white/95 ${cardPadding} text-slate-900 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl`}>
      <button
        type="button"
        onClick={handleClose}
        className="absolute top-4 right-4 z-30 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900/95 text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/40"
        aria-label="Close"
      >
        <X size={18} />
      </button>
      <div className="flex flex-col gap-6">
        <div className="space-y-3 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
            <MailCheck size={28} />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-slate-900">Verification code</h1>
            <p className="text-sm text-slate-600">
              Enter the code from the email
              {initialEmail ? (
                <>
                  {" "}
                  sent to <span className="font-semibold text-slate-900">{initialEmail}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleManualVerificationSubmit} noValidate>
          <div className={fieldWrapperClasses}>
            <Link2 size={20} className="text-slate-400" />
            <input
              id="verify-token"
              type="text"
              value={verificationTokenInput}
              onChange={onInputChange}
              className={inputClasses}
              placeholder="Paste your code here"
              aria-invalid={Boolean(verificationTokenError)}
            />
          </div>
          <button type="submit" className={primaryButtonClasses}>
            <Check size={22} />
            Verify email
          </button>
          {verificationTokenError ? (
            <p className="text-xs font-medium text-rose-500" role="alert">
              {verificationTokenError}
            </p>
          ) : submitted ? (
            <p className="text-xs text-slate-500">Checking your code…</p>
          ) : null}
        </form>
      </div>
    </div>
  );

  if (embedded) {
    return card;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="flex min-h-screen items-center justify-center px-4 py-8 md:px-6 md:py-10">
        {card}
      </div>
    </div>
  );
};

export default VerifyEmailPrompt;
